import { createSlice } from '@reduxjs/toolkit';
import { pushDynamicTab } from '../utils/dynamicTabs';

const SLICE_VERSION = '1.0';

const DEFAULT_LIST_VIEW_STATE = {
  currentPage: 1,
  locationsPerPage: 50,
};

const getAllTabs = (state) => [...state.defaultTabs, ...state.dynamicTabs];

// Record the tab the user is leaving so a later tab closure can return to it.
const pushTabHistory = (state, tabId) => {
  if (!tabId) return;
  state.tabHistory = state.tabHistory.filter((id) => id !== tabId);
  state.tabHistory.push(tabId);
};


export const initialState = {
  _version: SLICE_VERSION,

  defaultTabs: [
    { id: 'locations', name: 'Locations', contentType: 'locations' },
  ],

  dynamicTabs: [],
  activeTab: 0,
  // Stack of previously active tab ids, most-recent-last — lets removeTab send the
  // user back to whichever tab/page they were actually viewing before, not tab 0.
  tabHistory: [],

  listViewState: {
    'locations-table': { ...DEFAULT_LIST_VIEW_STATE },
  },
};

const locationSlice = createSlice({
  name: 'locations',
  initialState,

  reducers: {
    addTab: (state, action) => {
      const payload = action.payload || {};

      const tab = {
        id: payload.id || `dyn-${Date.now()}`,
        name: payload.name || `Location ${state.dynamicTabs.length + 1}`,
        isDynamic: true,
        createdAt: Date.now(),
        contentType: payload.contentType || null,
        locationId: payload.locationId || null,
        data: payload.data || null,
      };

      const prevActiveId = getAllTabs(state)[state.activeTab]?.id;
      const added = pushDynamicTab(state, tab, state.defaultTabs.length);
      if (added) pushTabHistory(state, prevActiveId);
    },

    removeTab: (state, action) => {
      const { tabIndex } = action.payload;
      if (tabIndex < state.defaultTabs.length) return;

      const tabsBefore = getAllTabs(state);
      const removedId = tabsBefore[tabIndex]?.id;
      const activeId = tabsBefore[state.activeTab]?.id;
      const wasActive = tabIndex === state.activeTab;

      const dynamicIndex = tabIndex - state.defaultTabs.length;
      state.dynamicTabs.splice(dynamicIndex, 1);
      state.tabHistory = state.tabHistory.filter((id) => id !== removedId);

      const tabsAfter = getAllTabs(state);

      if (!wasActive) {
        // The tab the user was viewing wasn't the one closed — stay on it, just
        // recompute its index since removing an earlier tab shifts the array.
        const newIndex = tabsAfter.findIndex((t) => t.id === activeId);
        state.activeTab = newIndex >= 0 ? newIndex : Math.min(state.activeTab, tabsAfter.length - 1);
        return;
      }

      // The active tab itself was closed — return to the last tab/page the user
      // was actually on, walking back through history until one still exists.
      while (state.tabHistory.length) {
        const candidateId = state.tabHistory.pop();
        const idx = tabsAfter.findIndex((t) => t.id === candidateId);
        if (idx >= 0) {
          state.activeTab = idx;
          return;
        }
      }

      // No usable history — fall back to the default Locations tab.
      state.activeTab = 0;
    },

    setActiveTab: (state, action) => {
      const { id, index } = action.payload;
      const tabs = getAllTabs(state);

      if (id) {
        const idx = tabs.findIndex((t) => t.id === id);
        if (idx !== -1 && idx !== state.activeTab) {
          pushTabHistory(state, tabs[state.activeTab]?.id);
          state.activeTab = idx;
        }
        return;
      }

      if (index != null) {
        const total = state.defaultTabs.length + state.dynamicTabs.length;
        if (index >= 0 && index < total && index !== state.activeTab) {
          pushTabHistory(state, tabs[state.activeTab]?.id);
          state.activeTab = index;
        }
      }
    },

    setListViewState: (state, action) => {
      const { stateKey = 'locations-table', ...rest } = action.payload || {};
      const existing = state.listViewState[stateKey] || { ...DEFAULT_LIST_VIEW_STATE };
      state.listViewState[stateKey] = { ...existing, ...rest };
    },

    resetTabs: (state) => {
      state.dynamicTabs = [];
      state.activeTab = 0;
      state.tabHistory = [];

    },
  },
});

export const { addTab, removeTab, setActiveTab, setListViewState, resetTabs } = locationSlice.actions;

export default locationSlice.reducer;
